import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setFormStatus('sending');
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6ggv85flgn4c71tl25g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #1a1a1a 0%, #2a2a2a 50%, #1a1a1a 100%)' }}
      dir="rtl"
    >
      {/* Background decorative */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-[-120px] left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #F5A623 0%, transparent 70%)' }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(#F5A623 1px, transparent 1px), linear-gradient(90deg, #F5A623 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{
          background: 'rgba(26,26,26,0.95)',
          borderBottom: '1px solid rgba(245,166,35,0.2)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/645339dfad8e1015d6f60a0aacde7112/7ac018fe95b5893e2914eabc8075ecee.png"
              alt="Logo"
              className="h-12 w-12 object-contain"
            />
            <span className="font-bold text-white text-sm hidden sm:block">للتأسيسات والسيطرة الكهربائية QC</span>
          </Link>
          <nav className="flex items-center gap-2">
            <Link
              to="/"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              <span>الرئيسية</span>
            </Link>
            <span
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white whitespace-nowrap"
              style={{ background: '#F5A623', boxShadow: '0 2px 12px rgba(245,166,35,0.4)' }}
            >
              <i className="ri-customer-service-2-line"></i>
              <span>تواصل معنا</span>
            </span>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20electrical%20engineering%20team%20working%20together%20in%20modern%20office%2C%20warm%20orange%20ambient%20lighting%2C%20industrial%20background%20with%20control%20panels%20and%20blueprints%2C%20high%20quality%20photography&width=1400&height=380&seq=contact-hero-01&orientation=landscape"
            alt="تواصل معنا"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70"></div>
        </div>
        <div className="relative z-10 w-full px-4 py-20 text-center text-white">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">تواصل معنا</h1>
        </div>
      </section>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-14 flex-1">

        {/* Contact Info - Full Width */}
        <div className="flex flex-col gap-6 max-w-xl mx-auto">
          <h2 className="text-2xl font-bold text-white">معلومات التواصل</h2>
          <div className="flex flex-col gap-3">
            <div
              className="flex items-center gap-3 p-4 rounded-xl border"
              style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(245,166,35,0.2)' }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-500/20 text-green-400 flex-shrink-0">
                <i className="ri-whatsapp-line text-xl"></i>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400">واتساب للأعمال فقط</p>
                <a href="https://wa.me/9647779577796" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-white hover:text-green-400 transition-colors cursor-pointer">
                  +964 777 957 7796
                </a>
              </div>
            </div>

            <div
              className="p-4 rounded-xl border"
              style={{ background: 'rgba(239,68,68,0.10)', borderColor: 'rgba(239,68,68,0.35)' }}
            >
              <p className="text-sm font-bold text-red-400 leading-8">
                <i className="ri-error-warning-line ml-2"></i>
                ملاحظة مهمة: رقم الواتساب مخصص للأعمال فقط، وليس للاستشارات. نرجو عدم إرسال
                الاستفسارات والاستشارات عبر الواتساب، ويمكنكم التواصل معنا بخصوصها عن طريق الإنستغرام.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {/* Location - plain text, no link */}
            <div
              className="flex items-center gap-4 p-4 rounded-xl border"
              style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.08)' }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: 'rgba(245,166,35,0.15)' }}>
                <i className="ri-map-pin-line text-xl text-[#F5A623]"></i>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">الموقع</p>
                <p className="text-sm font-semibold text-white">العراق، البصرة</p>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div>
            <p className="text-sm font-semibold text-gray-300 mb-3">تابعنا على</p>
            <div className="flex gap-3 flex-wrap">
              {[
                { href: 'https://web.facebook.com/qc.2qAlmuhandis/?locale=ar_AR', icon: 'ri-facebook-fill', color: '#1877F2', bg: 'rgba(24,119,242,0.15)', border: 'rgba(24,119,242,0.35)', title: 'فيسبوك' },
                { href: 'https://www.instagram.com/almuhandis_basra', icon: 'ri-instagram-line', color: '#E1306C', bg: 'rgba(225,48,108,0.15)', border: 'rgba(225,48,108,0.35)', title: 'إنستغرام' },
                { href: 'https://www.youtube.com/@Almuhandis.QC.2Q', icon: 'ri-youtube-line', color: '#FF0000', bg: 'rgba(255,0,0,0.15)', border: 'rgba(255,0,0,0.35)', title: 'يوتيوب' },
                { href: 'https://wa.me/9647779577796', icon: 'ri-whatsapp-line', color: '#25D366', bg: 'rgba(37,211,102,0.15)', border: 'rgba(37,211,102,0.35)', title: 'واتساب' },
                { href: 'https://www.tiktok.com/@almuhandis_basra', icon: 'ri-tiktok-fill', color: '#ffffff', bg: 'rgba(255,255,255,0.12)', border: 'rgba(255,255,255,0.25)', title: 'تيك توك' },
              ].map((s) => (
                <a
                  key={s.title}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.title}
                  className="w-[100px] h-[100px] flex items-center justify-center rounded-2xl transition-all cursor-pointer hover:scale-105"
                  style={{ background: s.bg, border: `1px solid ${s.border}` }}
                >
                  <i className={`${s.icon} text-4xl`} style={{ color: s.color }}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer
        className="relative z-10 py-5 text-center"
        style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
      >
        <p className="text-xs text-gray-500">
          تم البرمجة من قبل{' '}
          <span className="font-bold text-[#F5A623]">Murthdha abdulzahra</span>
        </p>
      </footer>
    </div>
  );
}
